import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, TrendingUp, Zap, Eye, Video } from "lucide-react";
import { getRankings } from "@/lib/api";
import { formatCompact } from "@/lib/format";
import SEO from "@/components/SEO";

const TABS = [
  { key: "subscribers", label: "Most Subscribers", icon: Trophy },
  { key: "views", label: "Most Views", icon: Eye },
  { key: "engagement", label: "Top Engagement", icon: Zap },
  { key: "score", label: "Best Score", icon: TrendingUp },
  { key: "videos", label: "Most Videos", icon: Video },
];

export default function Rankings() {
  const [tab, setTab] = useState("subscribers");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getRankings(tab).then(setRows).finally(() => setLoading(false));
  }, [tab]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
      <SEO
        title="Top YouTube Channel Rankings 2026 — Subscribers, Views, Engagement, Revenue"
        description="Live rankings of the top YouTube channels analyzed by our AI: most subscribers, highest engagement, best score, top revenue and most videos. Updated daily. Free access."
        path="/rankings"
        keywords="youtube channel rankings, biggest youtube channels, youtube subscriber tracker, youtube statistics, youtube growth tracker"
      />
      <h1 className="text-4xl font-black tracking-tight">Rankings</h1>
      <p className="mt-3 text-muted-foreground">Channels already analyzed on the platform, ranked by metric.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="rankings-tabs" className="grid grid-cols-2 md:grid-cols-5 h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`ranking-tab-${t.key}`} className="py-2.5">
              <t.icon className="w-4 h-4 mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split(" ").slice(-1)[0]}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-8 card-flat overflow-hidden">
        {loading && <div className="p-12 text-center text-muted-foreground">Loading...</div>}
        {!loading && rows.length === 0 && (
          <div data-testid="rankings-empty" className="p-12 text-center text-muted-foreground">
            No channels analyzed yet. Head over to <Link to="/analyze" className="text-primary underline">Analyze Channel</Link> to populate the rankings.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div data-testid="rankings-list" className="divide-y divide-border">
            {rows.map((r, i) => (
              <Link
                key={r.channel_id}
                to={`/channel/${r.channel_id}`}
                data-testid={`ranking-row-${i}`}
                className="flex items-center gap-4 p-4 hover:bg-secondary/40 transition-colors"
              >
                <div className="w-10 text-center font-mono text-2xl font-black text-muted-foreground">{i + 1}</div>
                {r.thumbnail && <img src={r.thumbnail} alt={`${r.title} avatar`} loading="lazy" className="w-12 h-12 rounded-lg border border-border" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{r.title}</div>
                  <div className="text-xs text-muted-foreground font-mono">{r.country || "—"}</div>
                </div>
                <div className="hidden sm:grid grid-cols-4 gap-6 text-right font-mono tabular-nums text-sm">
                  <div><div className="overline text-[10px]">Subs</div>{formatCompact(r.subscribers)}</div>
                  <div><div className="overline text-[10px]">Views</div>{formatCompact(r.total_views)}</div>
                  <div><div className="overline text-[10px]">Eng.</div>{r.engagement_rate}%</div>
                  <div><div className="overline text-[10px]">Score</div><span className="text-primary font-bold">{r.score}</span></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
